import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, Input, OnInit, Signal, signal, ViewChild } from '@angular/core';
import { API, AreaCategoryDTO, CompanyDTO, NamedIdentityDTO } from 'src/app/services/api/api';
import { ErrorService } from 'src/app/services/error.service';
import { UtilService } from 'src/app/services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { TagService } from '../../../services/tag.service';
import { AreaDTOAndTags } from '../../../models/taggable.model';
import { ModalNavigationComponent } from '../../../services/gui/modal/modal-navigation-component';
import { SbModalState } from '../../../services/gui/modal/modal-state.service';
import { SbSplitButton, SelectionState } from '../../../services/global/global-interfaces';
import { UserService } from '../../../services/user.service';
import { PermissionService } from '../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, switchMap, take } from 'rxjs/operators';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { RequestClass } from '../../../services/global/RequestClass';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelComponent, FilterPanelConfig } from '@ngx-seatback/layout';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { MultiSelectButtonAction } from '../../../components/table-multi-select-button/table-multi-select-button.component';

type SplitButtonAction = 'edit';

@Component({
  selector: 'sb-page-areas',
  templateUrl: './areas.component.html',
  styleUrls: ['./areas.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AreasComponent extends RequestClass implements OnInit, ModalNavigationComponent {
  public readonly errorHandler = inject(ErrorService);
  public readonly util = inject(UtilService);
  public readonly translate = inject(TranslateService);
  public readonly tagService = inject(TagService);
  public readonly userService = inject(UserService);
  public readonly permissionService = inject(PermissionService);
  protected readonly stringTransformation = inject(StringTransformationService);
  protected readonly api = inject(API);
  private readonly destroyRef = inject(DestroyRef);
  private readonly modalState = inject(SbModalState);

  @ViewChild('FilterPanelRef') filterPanelRef: FilterPanelComponent;

  @Input() isInModalContext: boolean;

  // table wrapper variables
  areas = signal<AreaDTOAndTags[]>([]);
  selectedAreas = signal<Map<number, AreaDTOAndTags>>(new Map());
  selectionState: Signal<SelectionState> = computed(() => {
    const currentlySelectedAreas = this.selectedAreas();
    const currentAreas = this.areas();
    if (!currentAreas || currentAreas.length === 0) {
      return 'none';
    }
    const allSelectedOnCurrentPage = currentAreas.every(item => currentlySelectedAreas.has(item.id));
    if (allSelectedOnCurrentPage) {
      return 'all';
    }
    const someSelectedOnCurrentPage = currentAreas.some(item => currentlySelectedAreas.has(item.id));
    if (someSelectedOnCurrentPage) {
      return 'partly';
    }
    return 'none';
  });

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    button: { visibleWithoutChanges: true },
  };
  selectedCompany: NamedIdentityDTO = null;
  filteredAreaCategories: AreaCategoryDTO[] = [];
  isGlobal: boolean | null = null;
  hasCompany: boolean | null = null;
  hasCategory: boolean | null = null;

  ngOnInit() {
    this.userService.selectedCompany.subscribe((company: CompanyDTO) => {
      if (company) {
        this.selectedCompany = company;
        this.requestDebounced();
      }
    });
  }

  selectAction($event: MultiSelectButtonAction) {
    const currentLocations = this.areas();
    if (!currentLocations || currentLocations.length === 0) {
      return;
    }
    const currentlySelectedLocations = this.selectedAreas();
    switch ($event) {
      case 'selectPage':
        currentLocations.forEach(item => {
          currentlySelectedLocations.set(item.id, item);
        });
        this.selectedAreas.set(new Map(currentlySelectedLocations));
        break;
      case 'deselectAll':
        this.deselectAreas();
        break;
      case 'deselectPage':
      default:
        currentLocations.forEach(item => {
          currentlySelectedLocations.delete(item.id);
        });
        this.selectedAreas.set(new Map(currentlySelectedLocations));
        break;
    }
  }

  selectItem(area: AreaDTOAndTags) {
    const currentlySelectedAreas = this.selectedAreas();
    if (currentlySelectedAreas.has(area.id)) {
      currentlySelectedAreas.delete(area.id);
      this.selectedAreas.set(new Map(currentlySelectedAreas));
    } else {
      currentlySelectedAreas.set(area.id, area);
      this.selectedAreas.set(new Map(currentlySelectedAreas));
    }
  }

  returnSelectedAreas() {
    if (this.isInModalContext && this.modalState.modals.get('areaSelectionModal')) {
      this.modalState.modals.get('areaSelectionModal').close(this.selectedAreas().values());
    }
  }

  deselectAreas() {
    this.selectedAreas.set(new Map());
  }

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .dm_geo_Area_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          global: this.isGlobal,
          hasCompany: this.hasCompany,
          hasCategory: this.hasCategory,
          category: this.util.arrayToIdArray(this.filteredAreaCategories),
          company: this.selectedCompany?.id ? [this.selectedCompany.id] : null,
        },
        this.pagination
      )
      .pipe(
        take(1),
        switchMap(areas => this.tagService.getAndAddTagEntitiesObs(this.tagService.TaggableDTO.Area, areas)),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (areas: AreaDTOAndTags[]) => {
          this.areas.set(this.buildSplitButtons(areas));
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.area_edit])) {
        item.sbButtons.push({
          icon: 'edit',
          tooltip: 'lang.globals.edit',
          link: RoutingID.areaEdit,
          pathParams: [{ id: 'areaID', value: item?.id }],
          queryParams: { returnTo: this.util.getPageLink() },
        });
      }
    });
    return result;
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
    this.isGlobal = filterMap.get('isGlobal')?.[0];
    this.hasCompany = filterMap.get('isGlobal')?.[0];
    this.hasCategory = filterMap.get('hasCategory')?.[0];
    this.selectedCompany = filterMap.get('companies')?.[0];
    this.filteredAreaCategories = filterMap.get('areaCategories');
  }
}
